import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";

type TabProps = {
  title: string;
  key: string;
};

type Props = {
  tabs: TabProps[];
  activeTabKey: string;
  onTabChange?: (tab: string) => void;
};

export const Tabs: FC<Props> = ({ tabs, activeTabKey, onTabChange }) => {
  const [selected, setSelected] = useState<string>();
  const [selectedProperties, setSelectedProperties] = useState<{
    width: string;
    x: string;
    transitionNeeded: boolean;
  }>();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);

  const handleResize = useCallback(
    (transitionNeeded: boolean) => {
      const selectedTabBounders =
        buttonRefs.current[selected]?.getBoundingClientRect();

      const tabsContainerBounder =
        tabsContainerRef.current?.getBoundingClientRect();

      const x =
        selectedTabBounders?.x -
        tabsContainerBounder?.x -
        (tabsContainerRef.current
          ? parseInt(getComputedStyle(tabsContainerRef.current).paddingLeft)
          : 0);

      setSelectedProperties({
        width: selectedTabBounders
          ? selectedTabBounders.width + "px"
          : undefined,
        x: x ? x + "px" : undefined,
        transitionNeeded: transitionNeeded,
      });
    },
    [selected],
  );

  useEffect(() => {
    let isFirstCall = true;
    const resizeObserver = new ResizeObserver(() => {
      handleResize(isFirstCall);
      isFirstCall = false;
    });

    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
    };
  }, [handleResize]);

  useEffect(() => {
    const activeTab = tabs?.find((tab) => tab.key === activeTabKey);

    if (activeTab) {
      setSelected(activeTab.key);
    } else if (tabs?.length > 1) {
      setSelected(tabs[0].key);
    }
  }, [activeTabKey, tabs]);

  const handleTabChange = (tabKey: string) => {
    setSelected(tabKey);

    if (onTabChange) {
      onTabChange(tabKey);
    }
  };

  return (
    <div
      className={classNames(
        "bg-primary-content/20 flex w-full items-center justify-between rounded-full p-1.5 backdrop-blur-lg",
        "lg:p-2",
      )}
      ref={tabsContainerRef}
    >
      {tabs.map(({ key, title }) => (
        <button
          key={key}
          onClick={() => handleTabChange(key)}
          ref={(el) => (buttonRefs.current[key] = el)}
          className={classNames(
            "text-small z-10 h-9 px-2 py-2 transition-colors duration-700",
            "xs:h-10 xs:px-3",
            "sm:text-regular sm:h-11",
            "md:px-5",
            "lg:h-15 lg:px-17 lg:py-4",
            {
              "text-base-200": selected === key,
              "text-primary-content": selected !== key,
            },
          )}
        >
          {title}
        </button>
      ))}

      <div
        className={classNames(
          "absolute h-9 translate-x-0 rounded-full bg-white",
          "xs:h-10",
          "sm:h-11",
          "lg:h-15",
          {
            "transition-all duration-700": selectedProperties?.transitionNeeded,
          },
        )}
        style={
          {
            "--tw-translate-x": selectedProperties?.x,
            width: selectedProperties?.width,
          } as React.CSSProperties
        }
      ></div>
    </div>
  );
};
